import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useGetUsersAdmin } from "@/features/users/api/get-users";
import { AddInvitationModal } from "@/features/users/components/add-invitation-modal";
import { UserSaRowActions } from "@/features/users/components/user-sa-row-actions";
import { UsersTable } from "@/features/users/components/users-table";
import { PublicUser } from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

export function AdminUsersPage() {
  const columnHelper = createColumnHelper<PublicUser>();
  const { pagination, setPagination, search, setSearch } = useTableControl();

  const { id: organisationId } = useParams();
  const {
    data: userData,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    isPlaceholderData,
    error,
  } = useGetUsersAdmin({
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    search,
    organisationId,
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        cell: (info) => {
          return `${info.row.original.firstName} ${info.row.original.lastName}`;
        },
        header: () => <span>Name</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("email", {
        id: "email",
        cell: (info) => info.getValue(),
        header: () => <span>Email</span>,
        enableSorting: false,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => {
          return <UserSaRowActions userId={info.row.original.id} />;
        },
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (isLoading)
    return (
      <div>
        <OnboardingHeader
          title="Manage Users"
          description="View and manage users"
          className="mb-6"
        />
        <LoadingScreen />
      </div>
    );

  if (isError || !isSuccess)
    return (
      <div>
        <OnboardingHeader
          title="Manage Users"
          description="View and manage users"
          className="mb-6"
        />
        <ErrorMessage error={error} />
      </div>
    );

  return (
    <div>
      <OnboardingHeader
        title="Manage Users"
        description="View and manage users"
        className="mb-6"
      />
      <div className="mb-2 justify-between sm:flex">
        <SearchBar
          search={search}
          setSearch={setSearch}
          className="mb-2 sm:mb-0 sm:max-w-xs"
        />

        <AddInvitationModal organisationId={organisationId} />
      </div>
      <UsersTable
        columns={columns}
        data={userData.items}
        pagination={pagination}
        pageCount={Math.ceil(userData.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </div>
  );
}
